import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { StatusPill } from "@/components/ui/status-pill";
import type { StatusTone } from "@/lib/status-tone";
import { useId } from "react";
import {
  type BusinessReceipt,
  type BusinessReconciliation,
  findPaymentRecordLink,
  isFinalRejection,
  PAYMENT_RECORD_KIND,
  rejectionReason,
  resourceLink,
} from "@/features/voice-agent/assistant-business-receipts";

const STATUS: Record<BusinessReceipt["status"], { label: string; tone: StatusTone }> = {
  canceled: { label: "Canceled", tone: "draft" },
  conflict: { label: "Safe to retry", tone: "warning" },
  created: { label: "Saved", tone: "success" },
  declined: { label: "Not repeated", tone: "draft" },
  expired: { label: "Expired", tone: "draft" },
  needs_input: { label: "Needs your answer", tone: "info" },
  ready: { label: "Prepared", tone: "draft" },
  rejected: { label: "Rejected", tone: "error" },
  superseded: { label: "Replaced", tone: "draft" },
  started: { label: "Running", tone: "processing" },
  unavailable: { label: "Unavailable", tone: "draft" },
  unknown: { label: "Not confirmed", tone: "warning" },
  waiting: { label: "Waiting", tone: "draft" },
};

/**
 * A refused execute is "Rejected" only for a persisted rejection code. Any other
 * code proves nothing about whether the command saved, so it reads as not
 * confirmed until the command's stored status is read.
 */
function statusOf(receipt: BusinessReceipt): { label: string; tone: StatusTone } {
  return receipt.status === "rejected" && !isFinalRejection(receipt.errorCode)
    ? STATUS.unknown
    : STATUS[receipt.status];
}

/** `account.create` reads as "Create account"; a command without one stays generic. */
function operationLabel(operation: string | undefined): string {
  const [resource, verb] = operation?.split(".") ?? [];
  if (!resource || !verb) return "Business command";
  const action = verb.replace(/_/g, " ");
  return `${action[0].toUpperCase()}${action.slice(1)} ${resource.replace(/_/g, " ")}`;
}

const RECORD_LINK_CLASS = "font-medium text-foreground underline underline-offset-2";

function savedText(kind: string | undefined) {
  return <span>The {kind?.replace(/_/g, " ") ?? "record"} was saved.</span>;
}

/** A payment receipt's page depends on the ledger table that holds its id. */
function SavedPayment({ id }: { id: string }) {
  const { data: link } = useQuery({
    queryFn: () => findPaymentRecordLink(id),
    queryKey: ["assistant-payment-record-link", id],
    retry: false,
    staleTime: 5 * 60_000,
  });
  return link ? (
    <Link className={RECORD_LINK_CLASS} to={link.path}>
      {link.label}
    </Link>
  ) : (
    savedText("payment")
  );
}

function SavedRecord({ receipt }: { receipt: BusinessReceipt }) {
  const link = resourceLink(receipt.resource, receipt.operation);
  if (link)
    return (
      <Link className={RECORD_LINK_CLASS} to={link.path}>
        {link.label}
      </Link>
    );
  return receipt.resource?.kind === PAYMENT_RECORD_KIND ? (
    <SavedPayment id={receipt.resource.id} />
  ) : (
    savedText(receipt.resource?.kind)
  );
}

const NOT_CONFIRMED_DETAIL = "It may have saved. Check the record before asking again.";

function ReceiptDetail({ receipt }: { receipt: BusinessReceipt }) {
  switch (receipt.status) {
    case "created":
      return <SavedRecord receipt={receipt} />;
    case "conflict":
      return (
        <span>
          A concurrent change blocked this attempt, so nothing was saved. The same command can run
          again safely.
        </span>
      );
    case "rejected":
      return isFinalRejection(receipt.errorCode) ? (
        <span>
          {["Nothing was saved and it will not run again.", rejectionReason(receipt.errorCode)]
            .filter(Boolean)
            .join(" ")}
        </span>
      ) : (
        <span>{NOT_CONFIRMED_DETAIL}</span>
      );
    case "unknown":
      return <span>{NOT_CONFIRMED_DETAIL}</span>;
    case "needs_input":
      return <span>{receipt.question ?? "The assistant needs your answer before it runs."}</span>;
    case "waiting":
      return <span>Not run yet. This step is saved and waits for the steps it depends on.</span>;
    case "ready":
      return <span>Not saved when checked.</span>;
    case "declined":
      return <span>You declined the repeat. Nothing was saved.</span>;
    case "superseded":
      return <span>Replaced by a later version. Nothing was saved by this one.</span>;
    case "canceled":
      return <span>Canceled before it ran. Nothing was saved.</span>;
    case "expired":
      return <span>Expired before it ran. Nothing was saved.</span>;
    case "unavailable":
      return <span>This record is no longer available.</span>;
    default:
      return <span>Waiting for the outcome.</span>;
  }
}

export function AssistantBusinessReceipt({ receipt }: { receipt: BusinessReceipt }) {
  const status = statusOf(receipt);
  return (
    <li className="grid gap-1 py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-sm">{operationLabel(receipt.operation)}</p>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </div>
      <p className="text-muted-foreground text-xs">
        <ReceiptDetail receipt={receipt} />
      </p>
    </li>
  );
}

function ReceiptList({
  labelledBy,
  receipts,
}: {
  labelledBy?: string;
  receipts: BusinessReceipt[];
}) {
  return (
    <ul
      aria-label={labelledBy ? undefined : "Commands in this reply"}
      aria-labelledby={labelledBy}
      className="divide-y divide-border/60"
    >
      {receipts.map((receipt) => (
        <AssistantBusinessReceipt key={receipt.commandId} receipt={receipt} />
      ))}
    </ul>
  );
}

function Reconciliation({ reconciliation }: { reconciliation: BusinessReconciliation }) {
  const headingId = useId();
  if (reconciliation.state === "loading")
    return <p className="text-muted-foreground text-xs">Checking recent business commands…</p>;
  if (reconciliation.state === "unavailable")
    return (
      <p className="text-muted-foreground text-xs">
        Recent business commands could not be loaded. Check the affected records before asking
        again.
      </p>
    );
  return (
    <div className="grid gap-2">
      <p className="font-medium text-sm" id={headingId}>
        Recent business commands
      </p>
      {reconciliation.receipts.length ? (
        <ReceiptList labelledBy={headingId} receipts={reconciliation.receipts} />
      ) : (
        <p className="text-muted-foreground text-xs">No recent business commands found.</p>
      )}
    </div>
  );
}

/** Receipts a turn produced, plus the recovery read after it failed. */
export function AssistantBusinessReceipts({
  receipts,
  reconciliation,
}: {
  receipts?: BusinessReceipt[];
  reconciliation?: BusinessReconciliation;
}) {
  if (!receipts?.length && !reconciliation) return null;
  return (
    <div
      aria-label="Business command receipts"
      className="mt-3 grid gap-3 border-t pt-3"
      role="group"
    >
      {receipts?.length ? <ReceiptList receipts={receipts} /> : null}
      {reconciliation && <Reconciliation reconciliation={reconciliation} />}
    </div>
  );
}
